import { Box, MenuItem, Select, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { fetchStockDashboardData } from "../../../../redux/reducers/stockDashboardSlice";
import { sortByMonthYearDesc } from "../../../../util/function";

// Bar Color Logic - moved outside component to prevent recreation
const getBarColor = (daysRemaining) => {
  if (daysRemaining <= 15) return "#EF4444";
  if (daysRemaining <= 30) return "#F59E0B";
  return "#22C55E";
};

const Stockbar = () => {
  const dispatch = useDispatch();
  const { expiryTracking, stockTurnOverRate, loading } = useSelector(
    (state) => state.stockDashboard
  );
  const [selectedYear, setSelectedYear] = useState("2025");
  const [expiryData, setExpiryData] = useState([]);
  const [turnoverData, setTurnoverData] = useState([]);

  // Memoize sorted data to prevent unnecessary recalculations
  const sortedStockTurnOverRate = useMemo(() => {
    return sortByMonthYearDesc(stockTurnOverRate ?? []);
  }, [stockTurnOverRate]);

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchStockDashboardData());
  }, [dispatch]);

  // Process expiry tracking data
  useEffect(() => {
    if (!Array.isArray(expiryTracking)) return;

    const topExpiry = (expiryTracking || []).slice(0, 8);
    const processed = topExpiry.map((item) => ({
      name: item.tradeName,
      value: item.daysRemaining,
      label: `Expiry Date: ${item.expiryDate}\nBatch No: ${item.batchNo},\nDays Remaining: ${item.daysRemaining}`,
      days: item.daysRemaining,
      color: getBarColor(item.daysRemaining),
    }));
    setExpiryData(processed);
  }, [expiryTracking]);

  useEffect(() => {
    if (!sortedStockTurnOverRate?.length) return;
    const reqData = sortedStockTurnOverRate?.map((li) => ({
      ...li,
      month: li?.month,
      total: li?.cogs,
      cogs: li?.cogs,
      turnover: li?.turnover,
      beginningStockValue: li?.beginningStockValue,
      endStockValue: li?.endStockValue,
      averageStockValue: li?.averageStockValue
    }));
    setTurnoverData(reqData);
  }, [sortedStockTurnOverRate]);

  // Memoize expiry chart categories and colors
  const expiryCategories = useMemo(() => expiryData.map((d) => d.name), [expiryData]);
  const expiryColors = useMemo(() => expiryData.map((d) => d.color), [expiryData]);
  const expiryValues = useMemo(() => expiryData.map((d) => d.value), [expiryData]);

  // Memoize tooltip formatter
  const expiryTooltipFormatter = useCallback(({ dataPointIndex }) => {
    const item = expiryData[dataPointIndex];
    if (!item) return '';
    return `<div style="padding: 10px; background: #fff; border: 1px solid #ccc; border-radius: 4px;">
              <strong>${item.name}</strong><br/>
              ${item.label.replace(/\n/g, "<br/>")}
            </div>`;
  }, [expiryData]);

  // Memoize data label formatter
  const expiryDataLabelFormatter = useCallback((val, opts) => {
    return `(${expiryData[opts.dataPointIndex]?.days} days)`;
  }, [expiryData]);

  // Expiry Chart Options - memoized
  const expiryChartOptions = useMemo(() => ({
    chart: {
      type: "bar",
      toolbar: { show: false },
      offsetY: 10,
      offsetX: 10
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "60%",
        distributed: true,
        borderRadius: 6,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: expiryDataLabelFormatter,
      offsetX: 180,
      style: {
        fontSize: "12px",
        fontWeight: "bold",
      },
    },
    xaxis: {
      categories: expiryCategories,
      max: 100,
      tickAmount: 5,
      labels: {
        show: true,
        style: { fontSize: "12px", colors: "#2C2C49" },
      },
    },
    yaxis: {
      labels: {
        show: true,
        maxWidth: 150,
        style: { fontSize: "12px", colors: "#2C2C49", fontWeight: "500" },
        formatter: function (val) {
          return val && val.length > 20 ? val.substring(0, 17) + "..." : val;
        }
      },
      tickAmount: expiryData.length,
    },
    grid: {
      show: true,
      strokeDashArray: 4,
      padding: { left: 20, right: 20 }
    },
    colors: expiryColors,
    fill: {
      type: "solid",
    },
    tooltip: {
      enabled: true,
      custom: expiryTooltipFormatter,
    },
    responsive: [{
      breakpoint: 768,
      options: {
        yaxis: {
          labels: {
            maxWidth: 100,
            formatter: function (val) {
              return val && val.length > 15 ? val.substring(0, 12) + "..." : val;
            }
          }
        }
      }
    }]
  }), [expiryCategories, expiryColors, expiryData.length, expiryDataLabelFormatter, expiryTooltipFormatter]);

  const expiryChartSeries = useMemo(() => [
    {
      data: expiryValues,
    },
  ], [expiryValues]);

  // Memoize turnover chart data
  const turnoverCategories = useMemo(() => turnoverData?.map((item) => item?.month) || [], [turnoverData]);
  const turnoverValues = useMemo(() => turnoverData?.map((item) => item?.total) || [], [turnoverData]);

  // Memoize turnover tooltip formatter
  const turnoverTooltipFormatter = useCallback(({ series, seriesIndex, dataPointIndex, w }) => {
    const item = turnoverData?.[dataPointIndex];
    if (!item) return '';

    const month = item?.month || 'Unknown';
    const cogs = Number(item?.cogs ?? 0)?.toLocaleString() || '0';
    const turnover = Number(item?.turnover ?? 0)?.toLocaleString() || '0';
    const beginningStockValue = Number(item?.beginningStockValue ?? 0)?.toLocaleString() || '0';
    const endStockValue = Number(item?.endStockValue ?? 0)?.toLocaleString() || '0';
    const averageStockValue = Number(item?.averageStockValue ?? 0)?.toLocaleString() || '0';
    const color = w?.globals?.colors?.[seriesIndex] || '#1D4ED8';

    return (
      '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
      month +
      '<span class="apexcharts-tooltip-marker" style="background-color: ' +
      color +
      '; margin-left: 5px; width: 12px; height: 12px; border-radius: 2px; display: inline-block;"></span>' +
      "</div>" +
      '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
      '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
      '<div class="apexcharts-tooltip-y-group" style="margin: 5px 0px;">' +
      '<span class="apexcharts-tooltip-text-y-label">COGS: </span>' +
      '<span class="apexcharts-tooltip-text-y-value" style="font-weight: 600;">' +
      cogs +
      "</span>" +
      "</div>" +
      '<div class="apexcharts-tooltip-y-group" style="margin: 5px 0px;">' +
      '<span class="apexcharts-tooltip-text-y-label">Turnover: </span>' +
      '<span class="apexcharts-tooltip-text-y-value" style="font-weight: 600;">' +
      turnover +
      "</span>" +
      "</div>" +
      '<div class="apexcharts-tooltip-y-group" style="margin: 5px 0px;">' +
      '<span class="apexcharts-tooltip-text-y-label">Beginning Stock Value: </span>' +
      '<span class="apexcharts-tooltip-text-y-value" style="font-weight: 600;">' +
      beginningStockValue +
      "</span>" +
      "</div>" +
      '<div class="apexcharts-tooltip-y-group" style="margin: 5px 0px;">' +
      '<span class="apexcharts-tooltip-text-y-label">End Stock Value: </span>' +
      '<span class="apexcharts-tooltip-text-y-value" style="font-weight: 600;">' +
      endStockValue +
      "</span>" +
      "</div>" +
      '<div class="apexcharts-tooltip-y-group" style="margin: 5px 0px;">' +
      '<span class="apexcharts-tooltip-text-y-label">Average Stock Value: </span>' +
      '<span class="apexcharts-tooltip-text-y-value" style="font-weight: 600;">' +
      averageStockValue +
      "</span>" +
      "</div>" +
      "</div>" +
      "</div>"
    );
  }, [turnoverData]);

  // Turnover Chart Options - memoized
  const turnoverChartOptions = useMemo(() => ({
    chart: {
      type: "line",
      toolbar: { show: false },
    },
    stroke: {
      width: 2,
      curve: "smooth",
    },
    xaxis: {
      categories: turnoverCategories,
    },
    legend: { show: true },
    markers: { size: 4 },
    yaxis: {
      labels: {
        formatter: (val) => `${val?.toLocaleString()}`,
      },
    },
    tooltip: {
      custom: turnoverTooltipFormatter,
    },
  }), [turnoverCategories, turnoverTooltipFormatter]);

  const turnoverChartSeries = useMemo(() => [
    {
      name: "Turnover",
      data: turnoverValues,
    },
  ], [turnoverValues]);

  if (loading && !expiryData.length && !turnoverData.length)
    return <Box p={3}>Loading charts...</Box>;

  return (
    <Box display="flex" gap={3} justifyContent="space-between" p={3} sx={{ flexWrap: "nowrap" }}>
      {/* Expiry Tracking */}
      {/* <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: 2,
          padding: 3,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Expiry Tracking
          </Typography>
        </Box>
        <Chart options={expiryChartOptions} series={expiryChartSeries} type="bar" height={350} />
      </Box> */}

      {/* Stock Turnover */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: 2,
          padding: 3,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Stock Turnover
          </Typography>
          {/* <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            size="small"
            variant="outlined"
          >
            {["2024", "2025"].map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select> */}
        </Box>
        <Chart options={turnoverChartOptions} series={turnoverChartSeries} type="line" height={280} />
      </Box>
    </Box>
  );
};

export default Stockbar;
